// src/pages/ChatHistory.js
import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  updateDoc
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { FiTrash2, FiEdit2 } from 'react-icons/fi';
import styles from '../styles/ChatHistory.module.css';

const ChatHistory = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedConv, setSelectedConv] = useState(null);
  const [draftName, setDraftName] = useState('');

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    setLoading(true);
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    try {
      const q = query(
        collection(db, 'users', userId, 'conversations'),
        where('participants', 'array-contains', userId)
      );
      const snapshot = await getDocs(q);
      const chats = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        date: docSnap.data().timestamp
          ? new Date(docSnap.data().timestamp.toDate()).toLocaleString()
          : ''
      }));
      setConversations(chats);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (conv) => {
    setSelectedConv(conv);
    setDraftName(conv.name || '');
    setIsEditOpen(true);
  };

  const openDeleteModal = (convId) => {
    setSelectedConv({ id: convId });
    setIsDeleteOpen(true);
  };

  const handleEditConfirm = async () => {
    const userId = auth.currentUser?.uid;
    if (!userId || !selectedConv) return;
    const newName = draftName.trim();
    if (!newName) return;
    try {
      const convRef = doc(db, 'users', userId, 'conversations', selectedConv.id);
      await updateDoc(convRef, { name: newName });
      setConversations(prev => prev.map(c => c.id === selectedConv.id ? { ...c, name: newName } : c));
    } catch (error) {
      console.error('Error updating conversation name:', error);
    } finally {
      setIsEditOpen(false);
      setSelectedConv(null);
    }
  };

  const handleDeleteConfirm = async () => {
    const userId = auth.currentUser?.uid;
    if (!userId || !selectedConv) return;
    try {
      await deleteDoc(doc(db, 'users', userId, 'conversations', selectedConv.id));
      setConversations(prev => prev.filter(c => c.id !== selectedConv.id));
    } catch (error) {
      console.error('Error deleting conversation:', error);
    } finally {
      setIsDeleteOpen(false);
      setSelectedConv(null);
    }
  };

  return (
    <div className={styles.container}>
      <h2>محفوظات المحادثة</h2>

      {/* Edit Modal */}
      {isEditOpen && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <h3>Rename Chat</h3>
            <input
              type="text"
              value={draftName}
              onChange={e => setDraftName(e.target.value)}
              className={styles.modalInput}
            />
            <div className={styles.modalActions}>
              <button onClick={() => setIsEditOpen(false)} className={styles.cancelBtn}>Cancel</button>
              <button onClick={handleEditConfirm} className={styles.confirmBtn}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {isDeleteOpen && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <h3>Delete Chat</h3>
            <p>Are you sure you want to delete this chat?</p>
            <div className={styles.modalActions}>
              <button onClick={() => setIsDeleteOpen(false)} className={styles.cancelBtn}>Cancel</button>
              <button onClick={handleDeleteConfirm} className={styles.deleteBtn}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p>جاري التحميل...</p>
      ) : conversations.length === 0 ? (
        <p>لا توجد محادثات سابقة</p>
      ) : (
        <div className={styles.conversationList}>
          {conversations.map(conv => (
            <div key={conv.id} className={styles.conversationItem}>
              <Link to={`/chat/${conv.id}`} className={styles.linkArea}>
                <span className={styles.date}>{conv.date}</span>
                <p className={styles.preview}>
                  {conv.name || conv.messages?.[0]?.message.substring(0, 50) || 'No preview'}...
                </p>
              </Link>
              <div className={styles.actions}>
                <FiEdit2
                  className={styles.icon}
                  title="Edit Chat Name"
                  onClick={() => openEditModal(conv)}
                />
                <FiTrash2
                  className={styles.icon}
                  title="Delete Chat"
                  onClick={() => openDeleteModal(conv.id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatHistory;

